import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Collapse, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api.js";
import Swal from 'sweetalert2';
import {
    FaThumbsUp,
    FaCheckCircle,
    FaArrowDown,
    FaChevronDown,
    FaChevronUp,
    FaFilter,
} from "react-icons/fa";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Line,
    LineChart,
    LabelList,
    ResponsiveContainer,
} from "recharts";

// Modal de Loading
function LoadingModal({ show, message }) {
    if (!show) return null;
    return (
        <div className="loading-modal-overlay">
            <div className="loading-modal-content">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: 15, fontSize: 18 }}>{message}</div>
            </div>
            <style>{`
                .loading-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1050;
                }
                .loading-modal-content {
                    background: white;
                    padding: 20px 40px;
                    border-radius: 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    box-shadow: 0 0 10px rgba(0,0,0,0.25);
                }
            `}</style>
        </div>
    );
}

// Formata moeda BR
const formatMoeda = (valor) =>
    valor?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Formata percentual BR
const formatPercent = (valor) => {
    if (isNaN(valor) || valor === null || valor === undefined) {
        return "N/A";
    }
    return `${Number(valor).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} %`;
};

const VendaMes = () => {
    const [showModal, setShowModal] = useState(true);
    const [showLojaModal, setShowLojaModal] = useState(false);
    const [mesSelecionado, setMesSelecionado] = useState("");
    const [dadosMes, setDadosMes] = useState([]);
    const [detalhesDia, setDetalhesDia] = useState({});
    const [loading, setLoading] = useState(false);
    const [lojas, setLojas] = useState([]);
    const [lojasSelecionadas, setLojasSelecionadas] = useState([]);

    const navigate = useNavigate();

    // Verifica acesso
    useEffect(() => {
        const usuario = localStorage.getItem("chapa");
        const permiteVendas = localStorage.getItem("permiteVendas");

        if (!usuario) {
            navigate("/arbor/login");
            return;
        }

        if (permiteVendas !== "S") {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Não é permitido acessar as vendas.',
                confirmButtonText: 'OK'
            }).then(() => {
                navigate("/arbor/home");
            });
        }
    }, [navigate]);

    // Mês atual
    useEffect(() => {
        const hoje = new Date();
        const aa = String(hoje.getFullYear()).slice(2);
        const mm = String(hoje.getMonth() + 1).padStart(2, "0");
        setMesSelecionado(`${aa}${mm}`);
    }, []);

    // Carregar lista de lojas
    useEffect(() => {
        const fetchLojas = async () => {
            try {
                const { data } = await api.get('/lojas/localizacao');
                const todasLojas = data.map(loja => String(loja.cod_emp).padStart(2, '0'));
                setLojas(data);
                setLojasSelecionadas(todasLojas);
            } catch (e) {
                console.error("Erro ao carregar lojas:", e);
                setLojas([]);
            }
        };
        fetchLojas();
    }, []);

    const carregarMes = async (mes, lojas) => {
        const usuario = localStorage.getItem("chapa");
        if (!mes || !usuario) return;

        setLoading(true);
        const lojasParam = lojas.length > 0 ? lojas.join(',') : '';
        try {
            const { data } = await api.get(
                `venda/mes/acum?mes=${mes}&usuario=${usuario}${lojasParam ? `&busca=${lojasParam}` : ''}`
            );
            setDadosMes(Array.isArray(data) ? data : []);
            setDetalhesDia({});
        } catch (e) {
            console.error("Erro carregando mês:", e);
            setDadosMes([]);
        }
        setLoading(false);
    };

    const carregarDia = async (dia, numdia) => {
        const usuario = localStorage.getItem("chapa");
        if (!usuario) return;

        setLoading(true);
        const lojasParam = lojasSelecionadas.length > 0 ? lojasSelecionadas.join(',') : '';

        if (detalhesDia[numdia]) {
            const copia = { ...detalhesDia };
            delete copia[numdia];
            setDetalhesDia(copia);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get(
                `venda/mes/loja?mes=${mesSelecionado}&usuario=${usuario}&dia=${dia}${lojasParam ? `&busca=${lojasParam}` : ''}`
            );
            setDetalhesDia({ ...detalhesDia, [numdia]: data });
        } catch (e) {
            console.error("Erro carregando dia:", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (mesSelecionado && lojasSelecionadas.length > 0) {
            carregarMes(mesSelecionado, lojasSelecionadas);
        }
    }, [mesSelecionado, lojasSelecionadas]);

    const handleMesChange = (valor) => {
        setMesSelecionado("");
        if (!valor) return;
        const [ano, mes] = valor.split("-");
        const aa = ano.slice(2);
        setMesSelecionado(`${aa}${mes}`);
    };

    const handleLojaChange = (cod_emp) => {
        setLojasSelecionadas(prevLojas => {
            if (prevLojas.includes(cod_emp)) {
                return prevLojas.filter(loja => loja !== cod_emp);
            } else {
                return [...prevLojas, cod_emp];
            }
        });
    };

    const handleSelectAllLojas = () => {
        const todasLojas = lojas.map(loja => String(loja.cod_emp).padStart(2, '0'));
        setLojasSelecionadas(todasLojas);
    };

    const handleClearAllLojas = () => {
        setLojasSelecionadas([]);
    };

    const chartHeight = 300;

    // Calcular totais para a nova linha da tabela com as fórmulas corretas
    const totalVenda = dadosMes.reduce((acc, curr) => acc + curr.venda, 0);
    const totalMeta = dadosMes.reduce((acc, curr) => acc + curr.meta, 0);
    const totalVlrMargem = dadosMes.reduce((acc, curr) => acc + (curr.venda * (curr.margem / 100)), 0);
    const totalVendaAcum = dadosMes.length > 0 ? dadosMes[dadosMes.length - 1].vda_vlr_acm : 0;
    const totalMetaAcum = dadosMes.length > 0 ? dadosMes[dadosMes.length - 1].vda_mta_acm : 0;

    // Cálculo do Atingido Dia (Soma Venda / Soma Meta * 100)
    const totalAtingidoDia = totalMeta > 0 ? (totalVenda / totalMeta) * 100 : "N/A";

    // Cálculo do Atingido Período (Soma Venda Acumulada / Soma Meta Acumulada * 100)
    const totalAtingidoPeriodo = totalMetaAcum > 0 ? (totalVendaAcum / totalMetaAcum) * 100 : "N/A";

    // Cálculo da Margem total (Soma da Margem Ponderada / Soma Venda * 100)
    const totalMargem = totalVenda > 0 ? (totalVlrMargem / totalVenda * 100) : "N/A";

    return (
        <div>
            <Menu modulo="ARBOR" />
            <div className="p-3 container-fluid">
                <h3 className="text-center mb-3">
                    Consulta de Vendas - {mesSelecionado}
                </h3>

                {/* Modal Seleção Mês */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Selecionar Mês</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <input
                            type="month"
                            className="form-control"
                            value={mesSelecionado ? `${"20" + mesSelecionado.slice(0, 2)}-${mesSelecionado.slice(2)}` : ''}
                            onChange={(e) => handleMesChange(e.target.value)}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Fechar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowModal(false);
                                carregarMes(mesSelecionado, lojasSelecionadas);
                            }}
                        >
                            Carregar
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal Seleção de Lojas */}
                <Modal show={showLojaModal} onHide={() => setShowLojaModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Selecionar Lojas</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <div className="d-flex justify-content-between mb-3">
                            <Button size="sm" onClick={handleSelectAllLojas}>Selecionar Todas</Button>
                            <Button size="sm" variant="outline-secondary" onClick={handleClearAllLojas}>Limpar Todas</Button>
                        </div>
                        <Form>
                            {lojas.map(loja => (
                                <Form.Check
                                    key={loja.cod_emp}
                                    type="checkbox"
                                    label={`Filial ${String(loja.cod_emp).padStart(2, '0')} - ${loja.razao}`}
                                    checked={lojasSelecionadas.includes(String(loja.cod_emp).padStart(2, '0'))}
                                    onChange={() => handleLojaChange(String(loja.cod_emp).padStart(2, '0'))}
                                />
                            ))}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowLojaModal(false)}>
                            Fechar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowLojaModal(false);
                                carregarMes(mesSelecionado, lojasSelecionadas);
                            }}
                        >
                            Aplicar Filtro
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Input de mês e botões - corrigido o layout */}
                <div className="mb-3 d-flex flex-column flex-sm-row gap-2 align-items-center">
                    <label className="me-2 mb-0">Mês/Ano:</label>
                    <input
                        type="month"
                        className="form-control"
                        style={{ maxWidth: '200px' }}
                        value={mesSelecionado ? `${"20" + mesSelecionado.slice(0, 2)}-${mesSelecionado.slice(2)}` : ''}
                        onChange={(e) => handleMesChange(e.target.value)}
                    />
                    <div className="d-flex gap-2">
                        <Button
                            onClick={() => carregarMes(mesSelecionado, lojasSelecionadas)}
                            disabled={loading}
                        >
                            {loading ? "Carregando..." : "Buscar"}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => setShowLojaModal(true)}
                        >
                            <FaFilter className="me-1" /> Lojas
                        </Button>
                    </div>
                </div>

                {/* Gráficos */}
                {Array.isArray(dadosMes) && dadosMes.length > 0 ? (
                    <div className="chart-container">
                        {/* Gráfico 1 - Meta atingida */}
                        <h5 className="mt-4">Meta atingida (%)</h5>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <BarChart data={dadosMes} margin={{ top: 30, right: 10, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="numdia"
                                    interval={0}
                                    angle={-90}
                                    textAnchor="end"
                                    height={50}
                                    style={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => formatPercent(value)} />
                                <Legend wrapperStyle={{ fontSize: "10px" }} />
                                <Bar dataKey="atingido" fill="#8884d8">
                                    <LabelList
                                        dataKey="atingido"
                                        angle={-90}
                                        position="insideTop"
                                        offset={10}
                                        formatter={(value) => formatPercent(value)}
                                        style={{ fontSize: 9, fill: '#000' }}
                                        textAnchor="end"
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>

                        {/* Gráfico 2 - Evolução Margem */}
                        <h5 className="mt-4">Evolução Margem (%)</h5>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart data={dadosMes} margin={{ top: 30, right: 10, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="numdia"
                                    interval={0}
                                    angle={-90}
                                    textAnchor="end"
                                    height={50}
                                    style={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => formatPercent(value)} />
                                <Legend wrapperStyle={{ fontSize: "10px" }} />
                                <Line type="monotone" dataKey="margem" stroke="#82ca9d">
                                    <LabelList
                                        dataKey="margem"
                                        angle={-90}
                                        position="insideTop"
                                        offset={-10}
                                        formatter={(value) => formatPercent(value)}
                                        style={{ fontSize: 9, fill: '#000' }}
                                        textAnchor="end"
                                    />
                                </Line>
                            </LineChart>
                        </ResponsiveContainer>

                        {/* NOVO GRÁFICO - Evolução Margem vs. Meta */}
                        <h5 className="mt-4">Evolução Margem vs. Meta (%)</h5>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart data={dadosMes} margin={{ top: 30, right: 10, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="numdia"
                                    interval={0}
                                    angle={-90}
                                    textAnchor="end"
                                    height={50}
                                    style={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => formatPercent(value)} />
                                <Legend wrapperStyle={{ fontSize: "10px" }} />
                                <Line type="monotone" dataKey="margem" stroke="#82ca9d" name="Margem Real">
                                    <LabelList
                                        dataKey="margem"
                                        angle={-90}
                                        position="insideTop"
                                        offset={-5}
                                        formatter={(value) => formatPercent(value)}
                                        style={{ fontSize: 9, fill: '#000' }}
                                        textAnchor="end"
                                    />
                                </Line>
                                <Line type="monotone" dataKey="margemmeta" stroke="#8884d8" name="Meta da Margem">
                                    <LabelList
                                        dataKey="margemmeta"
                                        angle={-90}
                                        position="insideTop"
                                        offset={-5}
                                        formatter={(value) => formatPercent(value)}
                                        style={{ fontSize: 9, fill: '#000' }}
                                        textAnchor="end"
                                    />
                                </Line>
                            </LineChart>
                        </ResponsiveContainer>

                        {/* Gráfico 3 - Meta atingida por período */}
                        <h5 className="mt-4">Meta atingida por período (%)</h5>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart data={dadosMes} margin={{ top: 30, right: 10, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="numdia"
                                    interval={0}
                                    angle={-90}
                                    textAnchor="end"
                                    height={50}
                                    style={{ fontSize: 10 }}
                                />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => formatPercent(value)} />
                                <Legend wrapperStyle={{ fontSize: "10px" }} />
                                <Line type="monotone" dataKey="atingidoper" stroke="#ff7300">
                                    <LabelList
                                        dataKey="atingidoper"
                                        angle={-90}
                                        position="insideTop"
                                        offset={-5}
                                        formatter={(value) => formatPercent(value)}
                                        style={{ fontSize: 9, fill: '#000' }}
                                        textAnchor="end"
                                    />
                                </Line>
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div className="text-center mt-5">
                        <p>Nenhum dado disponível para o mês selecionado.</p>
                    </div>
                )}

                {/* Tabela */}
                <div className="table-responsive mt-4">
                    <Table
                        striped
                        bordered
                        hover
                        size="sm"
                        className="text-center"
                    >
                        <thead>
                            <tr>
                                <th>Dia</th>
                                <th>Venda</th>
                                <th>Meta</th>
                                <th>Margem (%)</th>
                                <th>Atingido Dia</th>
                                <th>Atingido Período%</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(dadosMes) && dadosMes.length > 0 ? (
                                dadosMes.map((d) => (
                                    <React.Fragment key={d.numdia}>
                                        <tr>
                                            <td>{d.numdia}</td>
                                            <td>{formatMoeda(d.venda)}</td>
                                            <td>{formatMoeda(d.meta)}</td>
                                            <td>{formatPercent(d.margem)}</td>
                                            <td>
                                                {formatPercent(d.atingido)}
                                                {d.atingido > 100 ? (
                                                    <FaThumbsUp className="text-success ms-2" />
                                                ) : (
                                                    <FaArrowDown className="text-danger ms-2" />
                                                )}
                                            </td>
                                            <td>
                                                {formatPercent(d.atingidoper)}
                                                {d.atingidoper > 100 ? (
                                                    <FaCheckCircle className="text-success ms-2" />
                                                ) : (
                                                    <FaArrowDown className="text-danger ms-2" />
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    onClick={() => carregarDia(d.dia, d.numdia)}
                                                >
                                                    {detalhesDia[d.numdia] ? (
                                                        <FaChevronUp />
                                                    ) : (
                                                        <FaChevronDown />
                                                    )}
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="7" className="p-0 border-0">
                                                <Collapse in={!!detalhesDia[d.numdia]}>
                                                    <div>
                                                        {detalhesDia[d.numdia] && (
                                                            <div className="table-responsive">
                                                                <Table striped bordered hover size="sm" className="mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Filial</th>
                                                                            <th>Venda</th>
                                                                            <th>Meta</th>
                                                                            <th>Margem (%)</th>
                                                                            <th>Atingido</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {detalhesDia[d.numdia].map((loja, i) => (
                                                                            <tr key={i}>
                                                                                <td>{String(loja.filial).padStart(2, "0")}</td>
                                                                                <td>{formatMoeda(loja.venda)}</td>
                                                                                <td>{formatMoeda(loja.meta)}</td>
                                                                                <td>{formatPercent(loja.margem)}</td>
                                                                                <td>{formatPercent(loja.atingido)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7">Nenhum dado disponível.</td>
                                </tr>
                            )}
                            {/* Linha de totais */}
                            {dadosMes.length > 0 && (
                                <tr className="font-weight-bold">
                                    <td>TOTAL PERÍODO</td>
                                    <td>{formatMoeda(totalVenda)}</td>
                                    <td>{formatMoeda(totalMeta)}</td>
                                    <td>{formatPercent(totalMargem)}</td>
                                    <td>
                                        {formatPercent(totalAtingidoPeriodo)}
                                        {totalAtingidoPeriodo > 100 ? (
                                            <FaThumbsUp className="text-success ms-2" />
                                        ) : (
                                            <FaArrowDown className="text-danger ms-2" />
                                        )}
                                    </td>
                                    <td>
                                        {formatPercent(totalAtingidoPeriodo)}
                                        {totalAtingidoPeriodo > 100 ? (
                                            <FaCheckCircle className="text-success ms-2" />
                                        ) : (
                                            <FaArrowDown className="text-danger ms-2" />
                                        )}
                                    </td>
                                    <td></td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
            <Footer className="footer navbar-fixed-bottom" />
            <LoadingModal show={loading} message="Carregando..." />
        </div>
    );
};

export default VendaMes;